/* eslint-disable */
import { Editor } from '@ckeditor/ckeditor5-core';
import { Element } from '@ckeditor/ckeditor5-engine';
import { DiffItemAttribute, DiffItemInsert, DiffItemRemove } from '@ckeditor/ckeditor5-engine/src/model/differ';
import Document from '@ckeditor/ckeditor5-engine/src/model/document';
import Node from '@ckeditor/ckeditor5-engine/src/model/node';
import Text from '@ckeditor/ckeditor5-engine/src/model/text';
import { ReactComponent as RedoIcon } from '@ckeditor/ckeditor5-undo/theme/icons/redo.svg';
import { ReactComponent as UndoIcon } from '@ckeditor/ckeditor5-undo/theme/icons/undo.svg';
import EventInfo from '@ckeditor/ckeditor5-utils/src/eventinfo';
import { ChangeEvent, createRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import {
  postSectionsHeadingidcheck,
  postSectionsImgcheck,
  postSectionsSectionidcheck,
} from '../../../../../api/sections/sections';
import { getAxiosRequestConfig } from '../../../../../utils/api';
import SpinnerModal from '../../../../modules/SpinnerModal';
import Schema from './Schema';
import { UpdateElementHandler } from './SectionDataTreeNodeInterface';
import { SectionDataTreeRootNode } from './SectionDataTreeRootNode';
import { ValidationResults } from './ValidationResults';
import ViewAttributes from './view-attributes.json';

type DataTreeProps = {
  editor: Editor;
  sectionId: number | undefined;
  isPs?: boolean;
};

/**
 * SectionDataTree コンポーネントは、エディタのデータツリーを表示するためのコンポーネントです。
 * @param editor エディタインスタンス
 */
function SectionDataTree({ editor, sectionId, isPs }: DataTreeProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const axiosRequestConfig = getAxiosRequestConfig(language);
  const rootElement = editor.model.document.getRoot();
  const [selectedElement, setSelectedElement] = useState<Node | undefined>(undefined);
  const [scrollIntoElement, setScrollIntoElement] = useState<Node | undefined>(undefined);
  const [expandingItems, setExpandingItems] = useState<Element[]>([rootElement as Element]);
  const [displaySpinnerModal, setDisplaySpinnerModal] = useState<boolean>(false);

  // 子コンポーネントのRef
  const childRef = createRef<UpdateElementHandler>();

  // バリデーション結果
  const validationResults = useMemo(() => new ValidationResults(), []);

  /**
   * モデルの変更イベントハンドラ。
   */
  useEffect(() => {
    const onChangeData = (args: EventInfo<Document, 'change:data'>) => {
      const changes = args.source.differ.getChanges();
      if (changes.length > 0) {
        const diffItem = changes[0];
        switch (diffItem.type) {
          case 'insert':
            {
              const parent = (diffItem as DiffItemInsert).position.parent as Element;
              childRef.current?.updateElement(parent);
            }
            break;

          case 'remove':
            {
              const parent = (diffItem as DiffItemRemove).position.parent as Element;
              childRef.current?.updateElement(parent);
            }
            break;

          case 'attribute':
            {
              const parent = (diffItem as DiffItemAttribute).range.start.parent as Element;
              childRef.current?.updateElement(parent);
            }
            break;
        }
      }
    };

    editor.model.document.on('change:data', onChangeData);

    return () => {
      editor.model.document.off('change:data', onChangeData);
    };
  }, []);

  /**
   * ルート要素が変更されたら、開閉状態はクリアする。
   * 開始後にルート要素がなぜか変更される場合があるため。
   */
  useEffect(() => {
    setExpandingItems([rootElement as Element]);
  }, [rootElement]);

  /**
   * ツリーが表示されたときに、エディタの選択部分と展開を同期する。
   */
  useEffect(() => {
    handleSyncButtonClicked(undefined as unknown as React.MouseEvent<HTMLButtonElement>);
  }, []);

  /**
   * ツリーの要素の選択ハンドラ。
   * @param element 対象要素。
   */
  const handleSelectElement = (element: Node | undefined) => {
    try {
      if (element && !element.is('model:rootElement')) {
        editor.model.change((writer) => {
          if (element.is('$text')) {
            writer.setSelection(element, 'before');
          } else {
            writer.setSelection(element, 'on');
          }
        });

        editor.focus();

        editor.editing.view.scrollToTheSelection();
      }
    } catch (ex) {
      console.log(ex);
    }

    setSelectedElement(element);
  };

  /**
   * 属性表示チェックが保存されたものを取得する。
   */
  const savedViewAttributes = useMemo(() => {
    try {
      const savedViewAttributesString = localStorage.getItem('sectionDataTreeViewAttributes');
      if (!savedViewAttributesString) {
        return null;
      }

      const saveViewAttributes = JSON.parse(savedViewAttributesString);
      if (!Array.isArray(saveViewAttributes)) {
        return null;
      }

      return saveViewAttributes;
    } catch (e) {
      console.log(e);
      return null;
    }
  }, []);
  const [viewAttributes, setViewAttributes] = useState<string[]>(
    savedViewAttributes ?? ViewAttributes.defaultAttributes
  );

  /**
   * 属性表示チェックボックスが変更されたときに呼ばれる。
   * @param event イベント。
   */
  const handleViewAttributeCheckboxChanged = (event: ChangeEvent<HTMLInputElement>) => {
    let tempViewAttributes: string[];

    if (event.target.checked) {
      tempViewAttributes = [...viewAttributes, event.target.value];
    } else {
      tempViewAttributes = viewAttributes.filter((attribute) => attribute != event.target.value);
    }

    setViewAttributes(tempViewAttributes);
    localStorage.setItem('sectionDataTreeViewAttributes', JSON.stringify(tempViewAttributes));
  };

  /**
   * エディタ上の選択同期ボタンが押されたときに呼ばれる。
   * @param event
   */
  const handleSyncButtonClicked = (event: React.MouseEvent<HTMLButtonElement>) => {
    let toSelectElement = editor.model.document.selection.getSelectedElement();
    toSelectElement = toSelectElement ?? (editor.model.document.selection.anchor?.parent as Element);
    let items = expandingItems.concat();
    if (toSelectElement && !toSelectElement.is('$root')) {
      items = pushItemsToNode(toSelectElement, items);
    }

    setExpandingItems(items);
    setSelectedElement(toSelectElement);
    // スクロール対象の要素を設定して、要素を表示する。
    setScrollIntoElement(toSelectElement);
  };

  /**
   * スクロール対象の要素をリセットする。
   */
  const resetScrollIntoElement = () => {
    setScrollIntoElement(undefined);
  };

  /**
   * 指定要素へのルートとなる要素をすべて展開対象として追加する。
   * @param element 展開対象の要素。
   * @param items 展開対象の要素リスト。
   * @returns 展開対象の要素リスト。
   */
  const pushItemsToNode = (node: Element | Text, items: Element[]) => {
    let currentElement = (node.is('$text') ? node.parent : node) as Element;
    while (currentElement) {
      if (!items.includes(currentElement)) {
        items.push(currentElement);
      }

      if (currentElement.is('model:rootElement')) {
        break;
      }

      currentElement = currentElement.parent as Element;
    }

    return items;
  };

  /**
   * 要素のid属性を取得する。
   * @param element 対象要素。
   */
  const getIdAttribute = (node: Element | Text): string | undefined => {
    const ids = Array.from(node.getAttributes())
      .filter((value) => /^html.+Attributes$/.test(value[0]) && (value[1] as any)?.attributes?.id)
      .map((value) => (value[1] as any)?.attributes?.id);

    return ids.length > 0 ? ids[0] : undefined;
  };

  /**
   * 要素のid属性を収集する。
   * @param node 対象ノード。
   * @param idMap id属性とその要素のマップ。
   */
  const collectIds = (node: Element | Text, idMap: Map<string, Node[]>) => {
    const id = getIdAttribute(node);
    if (id) {
      if (idMap.has(id)) {
        idMap.get(id)?.push(node);
      } else {
        idMap.set(id, [node]);
      }
    }

    if (node.is('model:element')) {
      const children = Array.from(node.getChildren());
      children.forEach((child) => {
        collectIds(child, idMap);
      });
    }
  };

  /**
   * 要素のsection_id属性を取得する。
   * @param element 対象要素。
   */
  const getSectionIdAttribute = (node: Element | Text): string | undefined => {
    const ids = Array.from(node.getAttributes())
      .filter((value) => /^html.+Attributes$/.test(value[0]) && (value[1] as any)?.attributes?.section_id)
      .map((value) => (value[1] as any)?.attributes?.section_id);

    return ids.length > 0 ? ids[0] : undefined;
  };

  /**
   * 要素のsection_id属性を収集する。
   * @param node 対象ノード。
   * @param sectionIds section_id属性とその要素のリスト。
   */
  const collectSectionIds = (node: Element, sectionIds: { section_id: string; node: Element }[]) => {
    const section_id = getSectionIdAttribute(node);
    if (section_id) {
      sectionIds.push({ section_id, node });
    }

    if (node.is('model:element')) {
      const children = Array.from(node.getChildren());
      children.forEach((child) => {
        if (child.is('model:element')) {
          collectSectionIds(child, sectionIds);
        }
      });
    }
  };

  /**
   * 要素のsection_id属性のノードマップを取得する。
   * @param node 対象ノード。
   * @param sectionIds section_id属性とその要素のリスト。
   * @returns section_id属性とその要素のマップ。
   */
  const getSectionIdMap = (node: Element | Text, sectionIds: { section_id: string; node: Element }[]) => {
    const sectionIdMap = new Map<string, Element[]>();

    return sectionIds.reduce((map, { section_id, node }) => {
      if (section_id) {
        if (map.has(section_id)) {
          map.get(section_id)?.push(node);
        } else {
          map.set(section_id, [node]);
        }
      }

      return map;
    }, sectionIdMap);
  };

  /**
   * h?要素のidとsection_id属性を収集する。
   * @param node 対象ノード。
   * @param sectionId 現在のsection_id属性。
   * @param ids id属性とsection_id属性、ノードのリスト。
   */
  const collectIdsWithSectionId = (
    node: Element | Text,
    sectionId: string | undefined,
    ids: { id: string; section_id: number; node: Element }[]
  ) => {
    const section_id = getSectionIdAttribute(node);
    if (section_id) {
      sectionId = section_id;
    }

    if (node.is('model:element')) {
      if (sectionId) {
        switch (node.name) {
          case 'heading1':
          case 'heading2':
          case 'heading3':
          case 'heading4':
          case 'heading5':
            // 親要素がdiv.titleかどうかをチェック
            const parent = node.parent as Element;
            if (parent) {
              const htmlDivAttributes = parent.getAttribute('htmlDivAttributes') as any;
              if (Array.isArray(htmlDivAttributes?.classes)) {
                const classes = htmlDivAttributes?.classes as string[];
                if (classes.includes('title')) {
                  const id = getIdAttribute(node);
                  if (id) {
                    ids.push({ id: id, section_id: Number(sectionId), node: node });
                  }
                }
              }
            }
            break;
        }
      }

      const children = Array.from(node.getChildren());
      children.forEach((child) => {
        collectIdsWithSectionId(child, sectionId, ids);
      });
    }
  };

  /**
   * 要素のsrc属性を取得する。
   * @param element 対象要素。
   */
  const getSrcAttribute = (node: Element | Text): string | undefined => {
    return node.getAttribute('src') as string | undefined;
  };

  /**
   * img要素のsrc属性を収集する。
   * @param node 対象ノード。
   * @param srcs 要素とsrc属性のリスト。
   */
  const collectImgSrcs = (node: Element | Text, srcs: [Element, string][]) => {
    if (node.is('$text')) {
      return;
    }

    if (node.name === 'imageBlock' || node.name === 'imageInline') {
      const src = getSrcAttribute(node);
      if (src) {
        srcs.push([node, src]);
      }
    } else {
      const children = Array.from(node.getChildren());
      children.forEach((child) => {
        collectImgSrcs(child, srcs);
      });
    }
  };

  /**
   * 要素のバリデーションを再帰的に実行する。
   * @param node 対象要素。
   */
  const validateIndividualRecursive = (node: Element | Text) => {
    if (node.is('$text')) {
      return;
    }

    const elementDefinition = Schema.getElementDefinition(node.name);
    if (elementDefinition?.validate) {
      const [result, message] = elementDefinition.validate(node);
      if (!result) {
        validationResults.pushIndividualValidationResult(node, message);
      }
    } else {
      console.log(`バリデーション関数が定義されていません。name=${node.name}`);
    }

    Array.from(node.getChildren()).forEach((child) => {
      validateIndividualRecursive(child);
    });
  };

  /**
   * バリデーションボタンが押されたときに呼ばれる。
   * @param event イベント。
   */
  const handleValidateButtonClicked = async (event: React.MouseEvent<HTMLButtonElement>) => {
    setDisplaySpinnerModal(true);
    try {
      // 結果を初期化する。
      validationResults.clearAll();

      if (rootElement) {
        // 個別の検証を実行する。
        validateIndividualRecursive(rootElement as Element);

        // 全要素のid属性のうち、重複しているものを取得する。
        {
          const idMap = new Map<string, (Element | Text)[]>();
          collectIds(rootElement, idMap);

          const duplicatedIds = Array.from(idMap.entries()).filter(([_, nodes]) => nodes.length > 1);
          if (duplicatedIds.length > 0) {
            const message = duplicatedIds.forEach(([id, nodes]) => {
              nodes.forEach((node) => {
                validationResults.pushWholeValidationResult(node, `id属性「${id}」が重複しています。`);
              });
            });
          }
        }

        // 全要素のsection_id属性のうち、重複しているものを取得する。
        {
          const sectionIds: { section_id: string; node: Element }[] = [];
          collectSectionIds(rootElement as Element, sectionIds);

          const sectionIdMap = getSectionIdMap(rootElement, sectionIds);

          const duplicatedSectionIds = Array.from(sectionIdMap.entries()).filter(([_, nodes]) => nodes.length > 1);
          if (duplicatedSectionIds.length > 0) {
            const message = duplicatedSectionIds.forEach(([section_id, nodes]) => {
              nodes.forEach((node) => {
                validationResults.pushWholeValidationResult(node, `section_id属性「${section_id}」が重複しています。`);
              });
            });
          }

          // section_id属性が数値でないものを取得する。
          const invalidSectionIds = sectionIds
            .filter(({ section_id }) => section_id && !section_id.startsWith('#'))
            .filter(({ section_id }) => isNaN(Number(section_id)));
          if (invalidSectionIds.length > 0) {
            const message = invalidSectionIds.forEach(({ section_id, node }) => {
              validationResults.pushWholeValidationResult(node, `section_id属性「${section_id}」が数値でありません。`);
            });
          }

          // section_id属性をチェックする。
          if (sectionId) {
            const requestBody = sectionIds
              .filter(({ section_id }) => section_id && !section_id.startsWith('#') && !isNaN(Number(section_id)))
              .map(({ section_id }, index) => {
                return {
                  index,
                  section_id: Number(section_id),
                  result: true,
                  message: '',
                };
              });
            try {
              const response = await postSectionsSectionidcheck(
                sectionId,
                { section_id_infos: requestBody },
                axiosRequestConfig
              );
              if (response.status === 200) {
                const data = response.data;
                if (data) {
                  data.forEach((item: any) => {
                    if (!item.result && item.message) {
                      const node = sectionIds[item.index].node;
                      validationResults.pushWholeValidationResult(node, item.message);
                    }
                  });
                }
              }
            } catch (ex) {
              console.log(ex);
            }
          }
        }

        // h?要素のidをチェックする。
        if (sectionId) {
          const ids: { id: string; section_id: number; node: Element }[] = [];
          collectIdsWithSectionId(rootElement, undefined, ids);

          const requestBody = ids.map(({ id, section_id }, index) => {
            return {
              index,
              id,
              section_id,
              result: true,
              message: '',
            };
          });
          try {
            const response = await postSectionsHeadingidcheck(sectionId, { id_infos: requestBody }, axiosRequestConfig);
            if (response.status === 200) {
              const data = response.data;
              if (data) {
                data.forEach((item: any) => {
                  if (!item.result && item.message) {
                    const node = ids[item.index].node;
                    validationResults.pushWholeValidationResult(node, item.message);
                  }
                });
              }
            }
          } catch (ex) {
            console.log(ex);
          }
        }

        // img要素のsrc属性をAPIに問い合わせてチェックする。
        if (sectionId) {
          const srcs: [Element, string][] = [];
          collectImgSrcs(rootElement, srcs);

          const requestBody = srcs.map(([node, src], index) => {
            return { index: index, src: src, result: false, message: '' };
          });
          try {
            const response = await postSectionsImgcheck(sectionId, { srcs: requestBody }, axiosRequestConfig);
            if (response.status === 200) {
              const data = response.data;
              if (data) {
                data.forEach((item: any) => {
                  const node = srcs[item.index][0];
                  if (!item.result && item.message) {
                    validationResults.pushWholeValidationResult(node, item.message);
                  }
                });
              }
            }
          } catch (ex) {
            console.log(ex);
          }
        } else {
          console.log('section_idが取得できません。');
        }
      }

      // バリデーション結果を展開表示する。
      const validatedNodes = validationResults.getNodes();
      if (validatedNodes.length > 0) {
        let items = expandingItems.concat();
        validatedNodes.forEach((node) => {
          if (node.parent) {
            items = pushItemsToNode(node.parent as Element, items);
          }
        });

        setExpandingItems(items);

        // スクロール対象の要素を設定して、要素を表示する。
        setScrollIntoElement(validatedNodes[0]);
      }
    } finally {
      setDisplaySpinnerModal(false);
    }
  };

  /** undo, redo コマンドが実行可能かどうかを監視するフック */
  const useCommandEnabled = (editor: Editor, commandName: string) => {
    const command = editor.commands.get(commandName);
    const [isEnabled, setIsEnabled] = useState<boolean>(!!command?.isEnabled);

    useEffect(() => {
      if (!command) {
        setIsEnabled(false);
        return;
      }
      const update = () => setIsEnabled(command.isEnabled);
      update();
      command.on('change:isEnabled', update);
      return () => command.off('change:isEnabled', update);
    }, [command]);

    return isEnabled;
  };

  const undoEnabled = useCommandEnabled(editor, 'undo');
  const redoEnabled = useCommandEnabled(editor, 'redo');

  return (
    <>
      <div className="section-data-tree-container">
        <div className="section-data-tree-header">
          <div className="section-data-tree-buttons">
            <button onClick={() => editor.execute('undo')} title={editor.t('元に戻す')} disabled={!undoEnabled}>
              <UndoIcon width={16} height={16} />
            </button>
            <button onClick={() => editor.execute('redo')} title={editor.t('やり直し')} disabled={!redoEnabled}>
              <RedoIcon width={16} height={16} />
            </button>
          </div>
          <div className="section-data-tree-view-attributes">
            {!isPs && (
              <>
                {ViewAttributes.attributes.map((attribute) => {
                  const inputId = uuidv4();

                  return (
                    <span key={inputId}>
                      <input
                        id={inputId}
                        type="checkbox"
                        value={attribute}
                        checked={viewAttributes.includes(attribute)}
                        onChange={handleViewAttributeCheckboxChanged}
                      />
                      <label htmlFor={inputId}>{attribute}</label>
                    </span>
                  );
                })}
              </>
            )}
          </div>
          <div className="section-data-tree-buttons">
            <button onClick={handleSyncButtonClicked} title={editor.t('エディタ上の選択部分を展開して表示します。')}>
              <i className="fas fa-exchange-alt"></i>
            </button>
            {!isPs && (
              <button onClick={handleValidateButtonClicked} title={editor.t('高度なバリデーションを実行します。')}>
                <i className="fas fa-check-circle"></i>
              </button>
            )}
          </div>
        </div>
        <div className="section-data-tree-scroller">
          <div className="section-data-tree">
            {rootElement && (
              <SectionDataTreeRootNode
                ref={childRef}
                editor={editor}
                rootElement={rootElement}
                selectedElement={selectedElement}
                setSelectedElement={handleSelectElement}
                expandingItems={expandingItems}
                setExpandingItems={setExpandingItems}
                viewAttributes={viewAttributes}
                scrollIntoElement={scrollIntoElement}
                resetScrollIntoElement={resetScrollIntoElement}
                validationResults={validationResults}
                isPs={isPs}
              />
            )}
          </div>
        </div>
      </div>
      {displaySpinnerModal && <SpinnerModal />}
    </>
  );
}
export { SectionDataTree };
